import React from "react";
import { useHistory } from "../../providers/HistoryProvider";
import {
    ArrowPathIcon,
    ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import TemplatePicker from "../templates/TemplatePicker";
import { TemplateType } from "@neum/frontend-shared/constants/templateData";
import VetRecButton from "../common/VetRecButton";

const RecapErrorBanner: React.FC = () => {
    const {
        activeSession,
        preHistoryFailureMessage,
        recapSubmitting,
        templateRecap,
        setTemplateRecap,
        setError,
        handleSubmitRecap,
    } = useHistory();

    const isErrorRetriable = activeSession?.status.prehistory === "Failed";

    async function handleRegenRecap(): Promise<void> {
        if (!activeSession) {
            setError(
                "There was an issue processing the records. Please try again."
            );
            return;
        }

        try {
            await handleSubmitRecap({
                files: [],
                withFiles:
                    activeSession.prehistory_metadata?.with_files ?? false,
                isRegen: true,
                uploadId: activeSession.prehistory_metadata?.latest_upload_id,
            });
        } catch {
            setError(
                "There was an issue processing the records. Please try again."
            );
        }
    }

    if (!preHistoryFailureMessage) {
        return null;
    }

    return (
        <div className="rounded-md px-4 py-5 sm:p-6 flex flex-col sm:flex-row justify-between items-center bg-red-100 text-sm text-red-900 gap-y-4">
            <div className="flex flex-col sm:flex-row items-center gap-x-2 gap-y-2 font-semibold">
                <ExclamationCircleIcon className="text-red-700 h-6 w-6" />
                <div className="flex flex-col gap-x-2 gap-y-1 text-center sm:text-left">
                    <p>{preHistoryFailureMessage}</p>
                    {isErrorRetriable && <span className="font-medium">Please try again.</span>}
                </div>
            </div>
            {isErrorRetriable && (
                <div className="flex flex-col sm:flex-row items-center gap-x-2 gap-y-2 w-full sm:w-auto">
                    <TemplatePicker
                        template={templateRecap}
                        templateIdForce={
                            activeSession?.prehistory_metadata?.template_id
                        }
                        setTemplate={setTemplateRecap}
                        className="h-11"
                        templateBackground="bg-[linear-gradient(to_bottom,red-100_50%,white_50%)]"
                        type={TemplateType.RECORDSRECAP}
                    />
                    <VetRecButton
                        className="rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 text-white bg-red-700 hover:bg-red-600 items-center sm:w-auto w-full"
                        onClick={handleRegenRecap}
                        showProcessing={recapSubmitting}
                        spinnerClassName="fill-red-400"
                    >
                        {!recapSubmitting && <ArrowPathIcon className="mr-1 h-4 w-4 inline" />}
                        Retry
                    </VetRecButton>
                </div>
            )}
        </div>
    );
};

export default RecapErrorBanner;
